// services/severance.js — обезщетения при прекратяване на трудовото
// правоотношение: чл. 224 (неползван платен годишен отпуск), чл. 220
// (неспазено предизвестие) и чл. 222 (оставане без работа / болест /
// пенсиониране) от Кодекса на труда.
//
// ЧИСТИ функции: без база, без HTTP, без часовник — всичко идва като вход.
// Модулът се ползва и от тестовете (Node), и от фронтенд панела (Vite),
// затова тук няма нищо Node-специфично освен require/module.exports.
//
// Нормативна основа (да не се рови всеки път):
//   КТ чл. 154, 155–156а, 176, 176а, 177, 220, 222, 224, 228, 352;
//   НРВПО чл. 42; НСОРЗ чл. 17–21; НЕВДПОВ чл. 1, ал. 8, т. 7;
//   КСО чл. 6, ал. 3, чл. 9, ал. 3, т. 5 и ал. 5; ЗДДФЛ чл. 24 и 42;
//   ПМС 359/2024 и ПМС 243/2025 (МРЗ); фиксиран курс 1.95583 лв./€.

const holidays = require('./holidays');
const { round2 } = require('./totals');

// ---- Валутата -------------------------------------------------------------

// От 01.01.2026 сумите са в евро. Заплати от 2025 г. идват в лева и се
// превалутират по фиксирания (неизменен) курс на влизане в еврозоната.
const EUR_BGN_RATE = 1.95583;

function bgnToEur(amount) { return round2((Number(amount) || 0) / EUR_BGN_RATE); }
function eurToBgn(amount) { return round2((Number(amount) || 0) * EUR_BGN_RATE); }

// ---- Конфигурационни таблици ----------------------------------------------

// МРЗ за страната по периоди на действие. При ново ПМС се ДОБАВЯ ред —
// логиката не се пипа. Сумата е във валутата, действаща за периода.
const MIN_WAGE_TABLE = [
  { from: '2025-01-01', amount: 1077, currency: 'BGN' },   // ПМС 359/2024
  { from: '2026-01-01', amount: 620.20, currency: 'EUR' }, // ПМС 243/2025
];

// МРЗ към дата: последният ред, чието „от" е настъпило.
function minWageAt(dateIso) {
  const d = String(dateIso).slice(0, 10);
  let hit = null;
  for (const row of MIN_WAGE_TABLE) {
    if (row.from <= d && (!hit || row.from > hit.from)) hit = row;
  }
  if (!hit) throw new Error('Няма МРЗ в таблицата за дата ' + dateIso + ' — допълнете MIN_WAGE_TABLE.');
  return hit;
}

// Сметките вървят в евро — левовата МРЗ (2025) се превалутира тук.
function minWageEurAt(dateIso) {
  const w = minWageAt(dateIso);
  return w.currency === 'BGN' ? bgnToEur(w.amount) : w.amount;
}

// Осигуровки върху обезщетението по чл. 222, ал. 1 (КСО чл. 9, ал. 3, т. 5):
// дължат се САМО за фонд „Пенсии" (+УПФ за родените след 31.12.1959 г.),
// разпределени работодател/работник по чл. 6, ал. 3 КСО. Родените преди
// 1960 г. нямат УПФ — целият процент е във фонд „Пенсии", но сборът и
// разпределението съвпадат. По години, за да се допълва при промяна.
const INSURANCE_RATES = {
  2025: {
    pension: { total: 14.8, employer: 8.22, employee: 6.58 },
    upf: { total: 5, employer: 2.8, employee: 2.2 },
    pensionBefore1960: { total: 19.8, employer: 11.02, employee: 8.78 },
  },
  2026: {
    pension: { total: 14.8, employer: 8.22, employee: 6.58 },
    upf: { total: 5, employer: 2.8, employee: 2.2 },
    pensionBefore1960: { total: 19.8, employer: 11.02, employee: 8.78 },
  },
};

// Ставки за година: точната или последната по-ранна (2027 ползва 2026,
// докато таблицата не бъде допълнена — така нова година не чупи сметката).
function insuranceRatesFor(year) {
  const y = Number(year);
  let best = null;
  for (const k of Object.keys(INSURANCE_RATES)) {
    const ky = Number(k);
    if (ky <= y && (best === null || ky > best)) best = ky;
  }
  if (best === null) best = Math.min.apply(null, Object.keys(INSURANCE_RATES).map(Number));
  return INSURANCE_RATES[best];
}

// ---- Работни дни ----------------------------------------------------------

// Изключенията по календара идват или като прости дати ('YYYY-MM-DD' =
// неприсъствен ден), или в пълния формат на holidays.js ({date, kind}).
// Разместените от МС дни живеят на ниво фирма (calendar_overrides) и не са
// достъпни в чист модул — калкулаторът ги приема наготово като параметър.
function toOverrides(extraNonWorking) {
  return (extraNonWorking || []).map((x) => (
    typeof x === 'string'
      ? { date: x, kind: 'nonworking', reason: 'Изключение по календара' }
      : x
  ));
}

function pad2(n) { return (n < 10 ? '0' : '') + n; }

// Ден 0 на следващия месец = последният ден на този (без ръчна таблица
// с високосни години).
function lastDayOfMonth(year, month) {
  return new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
}

function workingDaysBetween(fromDate, toDate, extraNonWorking) {
  return holidays.workingDaysBetween(fromDate, toDate, toOverrides(extraNonWorking));
}

function workingDaysInMonth(year, month, extraNonWorking) {
  const from = year + '-' + pad2(Number(month)) + '-01';
  const to = year + '-' + pad2(Number(month)) + '-' + pad2(lastDayOfMonth(year, month));
  return workingDaysBetween(from, to, extraNonWorking);
}

// ISO низовете се сравняват като низове — за дати това е коректно.
function maxIso(a, b) { return a > b ? a : b; }
function minIso(a, b) { return a < b ? a : b; }

// ---- Чл. 224: дни неползван отпуск ----------------------------------------

// Полагаеми дни за една година по НРВПО чл. 42: при непълна година —
// годишният размер × пълни месеци / 12 (без междинно закръгляване; то е
// отделна настройка). Месецът е „пълен", когато са прослужени поне
// половината от РАБОТНИТЕ му дни (ал. 3).
//
// Болничен, майчинство и платен отпуск СЕ ЗАЧИТАТ за трудов стаж (чл. 352
// КТ), затова изобщо не са вход — смята се, все едно е работено. Вадят се
// само изрично „незачетени за стаж" дни (неплатен отпуск над зачитаните
// 30 раб. дни/г., самоотлъчки). Точното им разположение не се знае в чист
// модул, затова се приспадат ОТ КРАЯ на периода — консервативният избор
// при прекратяване, документиран и в панела.
function entitledDaysForYear(opts) {
  const y = Number(opts.year);
  // Липсващ размер → законовият минимум 20 (чл. 155, ал. 4). Изрично
  // подадена нула обаче се уважава — 0 не бива тихо да се превръща в 20.
  const annual = (opts.annualDays == null || opts.annualDays === '')
    ? 20
    : (Number(opts.annualDays) || 0);
  const hire = String(opts.hireDate || '').slice(0, 10);
  const term = String(opts.terminationDate || '').slice(0, 10);
  const start = maxIso(hire, y + '-01-01');
  const end = minIso(term, y + '-12-31');
  if (!hire || !term || start > end) return { months: 0, days: 0 };

  const served = [];
  for (let m = 1; m <= 12; m++) {
    const mStart = y + '-' + pad2(m) + '-01';
    const mEnd = y + '-' + pad2(m) + '-' + pad2(lastDayOfMonth(y, m));
    const oStart = maxIso(start, mStart);
    const oEnd = minIso(end, mEnd);
    served.push({
      wdMonth: workingDaysInMonth(y, m, opts.extraNonWorking),
      wdServed: oStart <= oEnd ? workingDaysBetween(oStart, oEnd, opts.extraNonWorking) : 0,
    });
  }

  let excluded = Math.max(0, Math.round(Number(opts.excludedServiceDays) || 0));
  for (let i = served.length - 1; i >= 0 && excluded > 0; i--) {
    const take = Math.min(served[i].wdServed, excluded);
    served[i].wdServed -= take;
    excluded -= take;
  }

  const months = served.filter((m) => m.wdMonth > 0 && m.wdServed >= m.wdMonth / 2).length;
  // 12 пълни месеца дават точно годишния размер — без дробен остатък.
  return { months, days: annual * months / 12 };
}

// Чл. 176а КТ: правото за година Г се погасява с изтичане на 2 години от
// края на Г — съществува през Г+1 и Г+2 и е погасено ОТ 01.01.(Г+3).
// При отлагане по чл. 176, ал. 1 давността тече от края на годината, в
// която е отпаднала причината (подава се като deferredCauseEndedYear).
function expiredByPrescription(opts) {
  const baseYear = Number(opts.deferredCauseEndedYear) || Number(opts.year);
  const cutoff = (baseYear + 3) + '-01-01';
  return String(opts.terminationDate).slice(0, 10) >= cutoff;
}

// Закръгляване на дробните дни: нормативно правило НЯМА (старото беше
// отменено от ВАС — ДВ бр. 18/2020), затова е настройка:
//   'math' — към най-близкия цял ден (по подразбиране);
//   'up'   — винаги нагоре (в полза на работника);
//   'none' — без закръгляване, 2 десетични знака.
function roundDays(days, rounding) {
  if (rounding === 'up') return Math.ceil(days - 1e-9); // float прахът да не качва 12.0000001 на 13
  if (rounding === 'none') return round2(days);
  return Math.round(days + Number.EPSILON);
}

// Дните по години → { perYear, totalDays, warnings }. Погасените по давност
// години се изключват от сбора, но остават в perYear с флаг — панелът ги
// показва с предупреждение, вместо тихо да ги изяде.
function vacationDays(opts) {
  const warnings = [];
  const perYear = [];
  let total = 0;

  for (const row of opts.years || []) {
    const year = Number(row.year);
    const expired = expiredByPrescription({
      year,
      terminationDate: opts.terminationDate,
      deferredCauseEndedYear: row.deferredCauseEndedYear,
    });
    const ent = entitledDaysForYear({
      year,
      hireDate: opts.hireDate,
      terminationDate: opts.terminationDate,
      annualDays: row.annualDays,
      excludedServiceDays: row.excludedServiceDays,
      extraNonWorking: opts.extraNonWorking,
    });
    const entitled = roundDays(ent.days, opts.rounding);
    const used = Number(row.usedDays) || 0;
    let remaining = opts.rounding === 'none' ? round2(entitled - used) : entitled - used;
    if (remaining < 0) {
      // Надползваният отпуск не се удържа от обезщетението (няма правно
      // основание за прихващане) — само предупреждаваме, не вадим.
      warnings.push('За ' + year + ' г. са ползвани ' + used + ' дни при полагаеми ' + entitled +
        ' — надползваният отпуск не се приспада от обезщетението за другите години.');
      remaining = 0;
    }
    if (expired) {
      warnings.push('Отпускът за ' + year + ' г. е погасен по давност (чл. 176а КТ) и е изключен от обезщетението.');
    } else {
      total += remaining;
    }
    perYear.push({ year, months: ent.months, entitled, used, remaining: expired ? 0 : remaining, expired });
  }

  if (opts.rounding === 'none') total = round2(total);
  return { perYear, totalDays: total, warnings };
}

// ---- Чл. 224: среднодневна ставка (чл. 177 КТ + НСОРЗ чл. 18–19) ----------

// mode 'base-month' (чл. 177, ал. 1): начисленото БТВ за последния месец с
// ≥10 отработени дни ÷ отработените дни в него. Месецът се търси назад без
// ограничение (при дълъг болничен може да е отпреди година+) — избира го
// ПОТРЕБИТЕЛЯТ, модулът получава готовите числа.
// mode 'contract' (чл. 177, ал. 2): няма такъв месец при този работодател —
// уговореното месечно ÷ средномесечния брой работни дни за годината на
// прекратяването.
function dailyRate(opts) {
  const notes = [];
  const term = String(opts.terminationDate).slice(0, 10);
  const termYear = Number(term.slice(0, 4));
  const termMonth = Number(term.slice(5, 7));
  const wdTermMonth = workingDaysInMonth(termYear, termMonth, opts.extraNonWorking);

  let rate = 0;
  let rateWithCoef = null;

  if (opts.mode === 'contract') {
    const wdYear = workingDaysBetween(termYear + '-01-01', termYear + '-12-31', opts.extraNonWorking);
    rate = round2((Number(opts.contractMonthly) || 0) / (wdYear / 12));
  } else {
    let gross = Number(opts.grossForBaseMonth) || 0;
    if (opts.grossCurrency === 'BGN') {
      // Заплата от 2025 г. е в лева; резултатът е в евро → фиксираният курс.
      const eur = bgnToEur(gross);
      notes.push('Начислените ' + gross.toFixed(2) + ' лв. са превалутирани на ' + eur.toFixed(2) +
        ' € по фиксирания курс 1.95583.');
      gross = eur;
    }
    const wd = Number(opts.workedDays) || 0;
    rate = wd > 0 ? round2(gross / wd) : 0;

    // Коефициентът по чл. 18, ал. 2 НСОРЗ (раб. дни на базовия месец ÷ раб.
    // дни на месеца на прекратяването): по буквата на наредбата се отнася за
    // месеца, „през който се ползва отпускът", и преобладаващото становище е,
    // че при обезщетение по чл. 224 НЕ се прилага. Смятаме го винаги, за да
    // могат да се покажат двете суми; ползването му е изричен избор
    // (applyCoefficient в calc224, по подразбиране изключен).
    if (opts.baseMonth && wdTermMonth > 0) {
      const wdBase = workingDaysInMonth(opts.baseMonth.year, opts.baseMonth.month, opts.extraNonWorking);
      rateWithCoef = round2(rate * wdBase / wdTermMonth);
    }
  }

  // Под по НСОРЗ чл. 19, ал. 2: ставката не може да е под МРЗ към датата на
  // прекратяването ÷ работните дни на месеца на прекратяването. Прилага се
  // само върху реално сметната ставка (0 значи празен вход, не „под пода").
  const floor = wdTermMonth > 0 ? round2(minWageEurAt(term) / wdTermMonth) : 0;
  let minWageFloorApplied = false;
  if (rate > 0 && rate < floor) {
    notes.push('Ставката ' + rate.toFixed(2) + ' € е под пода по минималната работна заплата и е вдигната на ' +
      floor.toFixed(2) + ' € (' + minWageEurAt(term).toFixed(2) + ' € ÷ ' + wdTermMonth +
      ' раб. дни; НСОРЗ чл. 19, ал. 2).');
    rate = floor;
    minWageFloorApplied = true;
  }
  if (rateWithCoef !== null && rateWithCoef > 0 && rateWithCoef < floor) rateWithCoef = floor;

  return { rate, rateWithCoef, minWageFloorApplied, floor, notes };
}

// ---- Срок за изплащане ----------------------------------------------------

// Чл. 228, ал. 3 КТ: обезщетенията по този раздел се изплащат до последния
// ден на месеца, СЛЕДВАЩ месеца на прекратяването; след това — законна
// лихва. Само информативно, нищо не се записва в календара на системата.
function paymentDueDate(terminationDate) {
  const d = String(terminationDate).slice(0, 10);
  const y = Number(d.slice(0, 4));
  const m = Number(d.slice(5, 7));
  // Ден 0 от по-следващия месец = последният ден на следващия.
  const last = new Date(Date.UTC(y, m + 1, 0));
  return last.getUTCFullYear() + '-' + pad2(last.getUTCMonth() + 1) + '-' + pad2(last.getUTCDate());
}

// ---- Чл. 224: крайната сметка ---------------------------------------------

// Приема обединените входове на vacationDays() и dailyRate() (+ rounding и
// applyCoefficient) и връща парите. Данък: 10% авансов по чл. 42 ЗДДФЛ —
// това обезщетение НЕ Е в изключенията на чл. 24, ал. 2, т. 8. Осигуровки
// не се дължат (НЕВДПОВ чл. 1, ал. 8, т. 7).
function calc224(opts) {
  const v = vacationDays(opts);
  const r = dailyRate(opts);
  const useCoef = !!opts.applyCoefficient && r.rateWithCoef !== null;
  const rate = useCoef ? r.rateWithCoef : r.rate;
  const days = v.totalDays;
  const gross = round2(rate * days);
  const tax = round2(gross * 0.10);
  const net = round2(gross - tax);
  const dueDate = paymentDueDate(opts.terminationDate);

  const notes = r.notes.slice();
  if (r.rateWithCoef !== null && r.rateWithCoef !== r.rate) {
    notes.push((useCoef
      ? 'Приложен е коефициентът по чл. 18, ал. 2 НСОРЗ: ставка ' + r.rateWithCoef.toFixed(2) + ' € (без него: ' + r.rate.toFixed(2) + ' €).'
      : 'Коефициентът по чл. 18, ал. 2 НСОРЗ не е приложен (преобладаващото становище за чл. 224); с него ставката би била ' + r.rateWithCoef.toFixed(2) + ' €.'));
  }
  notes.push('Осигуровки върху обезщетението по чл. 224 не се дължат (НЕВДПОВ чл. 1, ал. 8, т. 7).');
  notes.push('Данък: 10% авансов по чл. 42 ЗДДФЛ върху цялата сума.');
  notes.push('Срок за изплащане: до ' + dueDate.split('-').reverse().join('.') + ' (чл. 228, ал. 3 КТ); след това се дължи законна лихва.');

  return {
    days,
    rate,
    rateNoCoef: r.rate,
    rateWithCoef: r.rateWithCoef,
    minWageFloorApplied: r.minWageFloorApplied,
    gross,
    tax,
    net,
    dueDate,
    perYear: v.perYear,
    warnings: v.warnings,
    notes,
  };
}

// ---- Чл. 220: неспазено предизвестие --------------------------------------

// База по чл. 228, ал. 1: полученото месечно БТВ за месеца, предхождащ
// прекратяването (или последното получено). Изцяло неспазено предизвестие
// от N месеца → N × базата; частично (в работни дни) → среднодневно
// (базата ÷ раб. дни на месеца-база) × неспазените дни.
function calc220(opts) {
  const base = round2(opts.monthlyBase);
  const nu = opts.noticeUnserved || {};
  let gross = 0;
  let daily = null;

  if (Number(nu.months) > 0) {
    gross = round2(base * Number(nu.months));
  } else if (Number(nu.workDays) > 0 && opts.baseMonth) {
    const wd = workingDaysInMonth(opts.baseMonth.year, opts.baseMonth.month, opts.extraNonWorking);
    daily = wd > 0 ? round2(base / wd) : 0;
    gross = round2(daily * Number(nu.workDays));
  }

  const tax = round2(gross * 0.10);
  return {
    gross,
    tax,
    net: round2(gross - tax),
    daily,
    notes: [
      'Дължи се от страната, която не е спазила предизвестието — работодател или работник (чл. 220, ал. 1).',
      'Осигуровки не се дължат; данъкът е 10% авансов по чл. 42 ЗДДФЛ.',
    ],
  };
}

// ---- Чл. 222, ал. 1: оставане без работа ----------------------------------

// За всеки пълен месец без работа — 1 месечна база; за по-кратък период —
// среднодневно × работните дни без работа. Максимум 1 месец, по-дълъг срок
// само ако е уговорен (agreedMonths). Осигуровки: САМО фонд „Пенсии" (+УПФ
// за родените след 1959 г.) — КСО чл. 9, ал. 3, т. 5 и ал. 5; данъкът е
// върху брутото минус личните вноски.
function calc222_1(opts) {
  const notes = [];
  const base = round2(opts.monthlyBase);
  const agreed = Math.max(1, Number(opts.agreedMonths) || 1);
  let months = Math.max(0, Number(opts.months) || 0);
  if (months > agreed) {
    months = agreed;
    notes.push('Периодът е ограничен до уговорения срок от ' + agreed +
      ' месец(а) — по-дълъг се дължи само ако е изрично уговорен (чл. 222, ал. 1).');
  }
  let gross = round2(base * months);
  let daily = null;

  if (Number(opts.workDays) > 0 && months < agreed && opts.baseMonth) {
    const wd = workingDaysInMonth(opts.baseMonth.year, opts.baseMonth.month, opts.extraNonWorking);
    daily = wd > 0 ? round2(base / wd) : 0;
    gross = round2(gross + round2(daily * Number(opts.workDays)));
  } else if (Number(opts.workDays) > 0 && months >= agreed) {
    // Дните не изчезват мълчаливо — казваме защо не участват в сметката.
    notes.push('Работните дни извън уговорения срок от ' + agreed + ' месец(а) не се дължат (чл. 222, ал. 1).');
  }

  // По-нископлатена работа през периода: дължи се само РАЗЛИКАТА в
  // заплащането за отработените там дни (чл. 222, ал. 1, изр. 2).
  if (opts.lowerPaid && opts.lowerPaid.newMonthly != null && Number(opts.lowerPaid.workDays) > 0 && opts.baseMonth) {
    const wd = workingDaysInMonth(opts.baseMonth.year, opts.baseMonth.month, opts.extraNonWorking);
    const oldDaily = wd > 0 ? round2(base / wd) : 0;
    const newDaily = wd > 0 ? round2(round2(opts.lowerPaid.newMonthly) / wd) : 0;
    const diff = Math.max(0, round2(oldDaily - newDaily));
    gross = round2(gross + round2(diff * Number(opts.lowerPaid.workDays)));
    notes.push('Включена е разликата за по-нископлатената работа: ' + diff.toFixed(2) +
      ' €/ден × ' + opts.lowerPaid.workDays + ' дни.');
  }

  // Без подадена година (или изрични ставки) се взима последната от
  // таблицата — чистата функция няма часовник, от който да „познае".
  const fallbackYear = Math.max.apply(null, Object.keys(INSURANCE_RATES).map(Number));
  const rates = opts.rates || insuranceRatesFor(opts.year || fallbackYear);
  const personalPct = opts.bornBefore1960
    ? rates.pensionBefore1960.employee
    : rates.pension.employee + rates.upf.employee;
  const employerPct = opts.bornBefore1960
    ? rates.pensionBefore1960.employer
    : rates.pension.employer + rates.upf.employer;

  const personalInsurance = round2(gross * personalPct / 100);
  const employerInsurance = round2(gross * employerPct / 100);
  const taxBase = round2(gross - personalInsurance);
  const tax = round2(taxBase * 0.10);
  const net = round2(gross - personalInsurance - tax);

  notes.push(opts.bornBefore1960
    ? 'Осигуровки: само фонд „Пенсии" 19.8% (без УПФ — роден преди 01.01.1960 г.), лични ' + personalPct + '% / работодател ' + employerPct + '% (КСО чл. 9, ал. 3, т. 5 и ал. 5).'
    : 'Осигуровки: само фонд „Пенсии" и УПФ, лични ' + round2(personalPct) + '% / работодател ' + round2(employerPct) + '% (КСО чл. 9, ал. 3, т. 5 и ал. 5).');
  notes.push('Без вноски за ОЗМ, ТЗПБ, „Безработица" и здравно осигуряване.');
  notes.push('Периодът се зачита за осигурителен стаж (КСО чл. 9, ал. 3, т. 5).');
  notes.push('Данък 10% върху брутото минус личните осигуровки (чл. 42 ЗДДФЛ).');
  notes.push('Работодателските вноски (' + employerInsurance.toFixed(2) + ' €) са отделен разход на работодателя — не намаляват нетото.');

  return { gross, daily, personalInsurance, employerInsurance, taxBase, tax, net, notes };
}

// ---- Чл. 222, ал. 2: прекратяване поради болест ---------------------------

// 2 × месечната база при: ≥5 г. общ трудов стаж И необезщетяване на същото
// основание през последните 5 г. Необлагаемо (чл. 24, ал. 2, т. 8 ЗДДФЛ)
// и без осигуровки → нето = бруто.
function calc222_2(opts) {
  const notes = [];
  if (!opts.eligible5y) {
    notes.push('Изисква се поне 5 г. общ трудов стаж — условието не е изпълнено, обезщетение не се дължи (чл. 222, ал. 2).');
  }
  if (!opts.notReceivedLast5y) {
    notes.push('През последните 5 г. не трябва да е получавано обезщетение на същото основание — условието не е изпълнено (чл. 222, ал. 2).');
  }
  const ok = !!opts.eligible5y && !!opts.notReceivedLast5y;
  const gross = ok ? round2(2 * opts.monthlyBase) : 0;
  if (ok) notes.push('Необлагаемо (чл. 24, ал. 2, т. 8 ЗДДФЛ) и без осигуровки — нето = бруто.');
  return { gross, tax: 0, net: gross, notes };
}

// ---- Чл. 222, ал. 3: пенсиониране -----------------------------------------

// 2 × базата; 6 × при 10 г. трудов стаж при същия работодател или в същата
// група предприятия през последните 20 г. Плаща се само веднъж.
// Необлагаемо и без осигуровки → нето = бруто.
function calc222_3(opts) {
  const gross = round2((opts.tenYearsAtEmployer ? 6 : 2) * opts.monthlyBase);
  return {
    gross,
    tax: 0,
    net: gross,
    notes: [
      opts.tenYearsAtEmployer
        ? 'Шест месечни бази — 10 г. стаж при същия работодател/група през последните 20 г. (чл. 222, ал. 3).'
        : 'Две месечни бази (чл. 222, ал. 3); при 10 г. стаж при същия работодател/група стават шест.',
      'Изплаща се само веднъж — при първото пенсиониране.',
      'Необлагаемо (чл. 24, ал. 2, т. 8 ЗДДФЛ) и без осигуровки — нето = бруто.',
    ],
  };
}

// ============================ ФАЗА 2 =======================================
// Предизвестие (чл. 326 и 335), обезщетение по чл. 221 и прекратяване
// срещу уговорено обезщетение (чл. 331).

function bgDate(iso) { return String(iso).slice(0, 10).split('-').reverse().join('.'); }

// Същото число след N месеца; ако то не съществува (31-во в 30-дневен
// месец), се взима последният ден на месеца — иначе setUTCMonth прелива
// тихо към 1-во число на по-следващия (стар капан от календара).
function addMonths(iso, n) {
  const d = String(iso).slice(0, 10);
  // Регексът на панела пропуска „31.02" — тук е последната проверка, за да
  // не излезе краен срок за плащане върху несъществуваща дата.
  if (!holidays.isValidDate(d)) throw new Error('Невалидна дата: ' + iso);
  const y = Number(d.slice(0, 4));
  const m = Number(d.slice(5, 7));
  const day = Number(d.slice(8, 10));
  const total = (m - 1) + Number(n);
  const ty = y + Math.floor(total / 12);
  const tm = ((total % 12) + 12) % 12;
  const last = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
  return ty + '-' + pad2(tm + 1) + '-' + pad2(Math.min(day, last));
}

// Срок и дати на предизвестието (чл. 326, ал. 2 и 4; чл. 335, ал. 2, т. 1).
// Срок в ДНИ са календарни дни: при връчване на D тече от D+1 и изтича в
// края на ден D+N. Срок в МЕСЕЦИ изтича на съответното число на последния
// месец. Последният ден от срока е последният ден на правоотношението.
function noticePeriod(opts) {
  const served = String(opts.servedOn).slice(0, 10);
  const notes = [];
  const warnings = [];
  const days = Number(opts.noticeDays) || 0;
  const months = Number(opts.noticeMonths) || 0;

  const startsOn = holidays.addDays(served, 1);
  let expiresOn = months > 0 ? addMonths(served, months) : holidays.addDays(served, days);

  // Чл. 326, ал. 2: 30 дни ≤ срок ≤ 3 месеца при безсрочен договор.
  // Не режем стойността: датите се показват както са въведени, а човекът
  // вижда предупреждението — уговорка извън рамките е нищожна, но коя
  // стойност важи вместо нея е правен въпрос, не аритметичен.
  if (days > 0 && days < 30) {
    warnings.push('Срокът на предизвестието при безсрочен договор е минимум 30 дни (чл. 326, ал. 2).');
  }
  if (months > 3 || days > 92) {
    warnings.push('Срокът на предизвестието е максимум 3 месеца (чл. 326, ал. 2)' +
      (days > 92 ? ' — 3 месеца са най-много 92 календарни дни.' : '.'));
  }

  if (opts.contractEndDate) {
    const end = String(opts.contractEndDate).slice(0, 10);
    if (expiresOn > end) {
      expiresOn = end;
      notes.push('Срокът е ограничен до края на срочния договор (' + bgDate(end) +
        ') — предизвестието не може да е повече от остатъка от срока (чл. 326, ал. 2).');
    }
  }

  const terminationDate = expiresOn;

  // Празен срок (брой 0) или краен срок на договора преди връчването дават
  // „изтичане" преди началото — такива дати са безсмислени и се казва защо,
  // вместо тихо да се показва прекратяване в деня на връчването.
  if (expiresOn < startsOn) {
    warnings.push('Срокът изтича преди да е започнал — проверете броя (трябва да е поне 1) ' +
      'или крайния срок на договора (преди датата на връчване е).');
  }

  notes.push('Правоотношението се прекратява с изтичането на срока на предизвестието ' +
    '(чл. 335, ал. 2, т. 1); при неспазване — с изтичането на съответната част от срока (т. 2); ' +
    'без предизвестие — от получаването на изявлението (т. 3).');
  notes.push('Оттегляне на предизвестието: до или едновременно с получаването — свободно; ' +
    'след това — само със съгласие на другата страна (чл. 326, ал. 4).');
  notes.push('При материално-отчетническа длъжност предаването на повереното имущество може да ' +
    'удължи общото време с до 2 месеца (чл. 326, ал. 3) — бележка, не влиза в сметките.');

  const out = { startsOn, expiresOn, terminationDate, notes, warnings };
  if (opts.today) {
    const today = String(opts.today).slice(0, 10);
    // Календарните дни са прост остатък; работните минават през календара
    // на holidays.js — днешният ден вече тече, затова броим от утре.
    out.remainingCalendarDays = today < expiresOn
      ? Math.round((Date.parse(expiresOn) - Date.parse(today)) / 86400000)
      : 0;
    out.remainingWorkDays = today < expiresOn
      ? workingDaysBetween(holidays.addDays(today, 1), expiresOn, opts.extraNonWorking)
      : 0;
  }
  return out;
}

// Общото ядро на чл. 221: при безсрочен договор — БТВ „за срока на
// предизвестието"; при срочен — действителните вреди (ал. 4) като
// среднодневно × работни дни, с таван работните дни до края на договора.
// 30-дневното предизвестие струва точно една месечна база: законът говори
// за възнаграждението ЗА СРОКА (календарен), не за отработваеми дни —
// затова пропорцията при друг брой дни е към 30, а не по работни дни.
function calc221Core(opts) {
  const notes = [];
  const base = round2(opts.monthlyBase);
  let gross = 0;
  let daily = null;
  let days = null;

  if (opts.indefinite) {
    const months = Number(opts.noticeMonths) || 0;
    const nDays = Number(opts.noticeDays) || 0;
    gross = months > 0 ? round2(base * months) : round2(base * nDays / 30);
  } else {
    const wd = opts.baseMonth
      ? workingDaysInMonth(opts.baseMonth.year, opts.baseMonth.month, opts.extraNonWorking)
      : 0;
    daily = wd > 0 ? round2(base / wd) : 0;
    const dmg = opts.damages || {};
    days = Math.max(0, Number(dmg.daysWithoutWork) || 0);
    if (daily === 0 && days > 0) {
      notes.push('Среднодневната ставка е 0 — попълнете месец и година на базата (чл. 228, ал. 1), иначе вредите не могат да се остойностят.');
    }
    if (dmg.contractEndDate && opts.terminationDate) {
      const from = holidays.addDays(String(opts.terminationDate).slice(0, 10), 1);
      const end = String(dmg.contractEndDate).slice(0, 10);
      // workingDaysBetween РАЗМЕСТВА краищата при обърнат интервал — без
      // тази проверка прекратяване в/след края на договора дава фантомен
      // таван от няколко дни вместо нула.
      const cap = from <= end ? workingDaysBetween(from, end, opts.extraNonWorking) : 0;
      if (days > cap) {
        days = cap;
        notes.push(cap === 0
          ? 'Прекратяването е в деня на изтичане на договора или след него — вреди за период след края му не се дължат.'
          : 'Вредите са ограничени до остатъка от срока на договора: ' + cap +
            ' работни дни — обезщетението се дължи най-много до края на срочния договор.');
      }
    } else if (days > 0) {
      notes.push('Таванът „до остатъка от срока на договора" НЕ е приложен — липсва дата на ' +
        'прекратяване и/или краен срок на договора.');
    }
    gross = round2(daily * days);
  }
  return { gross, daily, days, notes };
}

// Чл. 221, ал. 1: РАБОТНИКЪТ получава — при прекратяване от него без
// предизвестие по чл. 327, ал. 1, т. 2, 3 и 3а (работодателят бави заплати,
// промени мястото/характера на работата и др.).
function calc221_1(opts) {
  const c = calc221Core(opts);
  const tax = round2(c.gross * 0.10);
  return {
    gross: c.gross,
    daily: c.daily,
    days: c.days,
    tax,
    net: round2(c.gross - tax),
    direction: 'employee-receives',
    notes: c.notes.concat([
      'Дължи се от работодателя при прекратяване от работника без предизвестие по чл. 327, ал. 1, т. 2, 3 и 3а (чл. 221, ал. 1).',
      'Осигуровки не се дължат (НЕВДПОВ чл. 1, ал. 8, т. 7); данъкът е 10% авансов (чл. 42 ЗДДФЛ — обезщетението не е сред необлагаемите).',
    ]),
  };
}

// Чл. 221, ал. 2 и 3: РАБОТНИКЪТ дължи — при дисциплинарно уволнение или
// уволнение по чл. 330, ал. 1 (осъждане за престъпление, което е и
// нарушение на трудовите задължения). Това е вземане на работодателя, не
// доход на работника — без данък и осигуровки.
function calc221_2(opts) {
  const c = calc221Core(opts);
  return {
    gross: c.gross,
    daily: c.daily,
    days: c.days,
    tax: 0,
    net: c.gross,
    direction: 'employee-owes',
    notes: c.notes.concat([
      opts.basis === 'conviction'
        ? 'Основание: уволнение по чл. 330, ал. 1 поради осъждане — режимът е същият като при дисциплинарното (чл. 221, ал. 3).'
        : 'Основание: дисциплинарно уволнение (чл. 221, ал. 2).',
      'Сумата е вземане на работодателя, не доход — данък и осигуровки няма.',
      'НЕ може да се удържа от заплатата или обезщетенията без ПИСМЕНО съгласие на работника: ' +
      'чл. 272, ал. 1 КТ не включва това вземане. Събира се доброволно или по съдебен ред.',
    ]),
  };
}

// Чл. 331: прекратяване по инициатива на работодателя срещу уговорено
// обезщетение — минимум 4 последни месечни БТВ (ал. 2); работникът отговаря
// писмено в 7-дневен срок (ал. 1); плаща се до 1 месец от прекратяването,
// иначе основанието се смята за отпаднало (ал. 3).
function calc331(opts) {
  const warnings = [];
  const notes = [
    'Работникът трябва да приеме предложението ПИСМЕНО в 7-дневен срок — мълчанието се смята за отказ (чл. 331, ал. 1).',
  ];
  // Празно/невалидно поле НЕ е „уговорка за 4" — то значи „няма уговорка",
  // при което важи законовият размер; казва се изрично, не тихо.
  const raw = Number(opts.agreedMultiplier);
  let mult;
  if (!Number.isFinite(raw) || raw <= 0) {
    mult = 4;
    notes.push('Без валидна уговорка се прилага законният размер: 4 последни месечни БТВ (чл. 331, ал. 2).');
  } else if (raw < 4) {
    mult = 4;
    warnings.push('Уговорка под 4-кратния размер не се допуска — чл. 331, ал. 2 изисква „не ' +
      'по-малко от 4-кратния размер на последното месечно БТВ"; приложен е законният минимум 4.');
  } else {
    mult = raw;
  }
  const gross = round2(mult * opts.lastMonthlyGross);
  const tax = round2(gross * 0.10);
  const term = String(opts.terminationDate || '').slice(0, 10);
  const payDeadline = term ? addMonths(term, 1) : null;

  // Датата на крайния срок се показва от ПАНЕЛА в откроен ред (payDeadline)
  // — тук е само правилото, иначе съобщението излизаше двойно.
  notes.push('Ако обезщетението не се плати в едномесечния срок от прекратяването, основанието се ' +
    'смята за ОТПАДНАЛО (чл. 331, ал. 3).');
  notes.push('Осигуровки не се дължат (НЕВДПОВ чл. 1, ал. 8, т. 7); данъкът е 10% (чл. 42 ЗДДФЛ).');
  notes.push('Обезщетение за безработица: НОИ третира основанието като взаимно съгласие — паричното ' +
    'обезщетение от НОИ е в минимален размер и срок. Само информация, не влиза в сметката.');

  return { gross, tax, net: round2(gross - tax), payDeadline, notes, warnings };
}

module.exports = {
  EUR_BGN_RATE,
  bgnToEur,
  eurToBgn,
  MIN_WAGE_TABLE,
  minWageAt,
  minWageEurAt,
  INSURANCE_RATES,
  insuranceRatesFor,
  workingDaysInMonth,
  workingDaysBetween,
  entitledDaysForYear,
  expiredByPrescription,
  vacationDays,
  dailyRate,
  paymentDueDate,
  calc224,
  calc220,
  calc222_1,
  calc222_2,
  calc222_3,
  // Фаза 2: предизвестие и прекратяване
  addMonths,
  noticePeriod,
  calc221_1,
  calc221_2,
  calc331,
};
